import Component from "@glimmer/component";
import { service } from "@ember/service";
import type IntlService from "ember-intl/services/intl";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import EmptyStateIntro, { type EmptyStateIntroSignature } from "glesys-controlpanel/components/empty-state/intro";
import EmptyStatePoints from "glesys-controlpanel/components/empty-state/points";
import EmptyStateActions, { type EmptyStateActionsSignature } from "glesys-controlpanel/components/empty-state/actions";

export interface EmptyStateSignature {
  Args: {
    icon?: EmptyStateIntroSignature["Args"]["icon"];
    title?: string;
    description?: string;
    points?: readonly string[];
    learnMoreUrl?: string;
    learnMoreAriaLabel?: string;
    createRoute?: string;
    createLabel?: string;
    createColor?: EmptyStateActionsSignature["Args"]["createColor"];
    onCreate?: () => void;
    contactUrl?: string;
  };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

export default class EmptyState extends Component<EmptyStateSignature> {
  @service declare intl: IntlService;

  get learnMoreAriaLabel() {
    return this.args.learnMoreAriaLabel ?? this.intl.t("button.learn-more-aria-label");
  }

  get createLabel() {
    return this.args.createLabel ?? this.intl.t("button.create");
  }

  get hasBody() {
    return Boolean(
      this.args.points?.length ||
      this.args.learnMoreUrl ||
      this.args.createRoute ||
      this.args.onCreate ||
      this.args.contactUrl,
    );
  }

  <template>
    <div
      class="flex flex-col items-center justify-center text-center gap-2 py-12 px-6 border border-dashed rounded-sm border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-900"
      data-test-empty-state
      {{mergeClasses}}
      ...attributes
    >
      <EmptyStateIntro @icon={{@icon}} @title={{@title}} @description={{@description}} />
      {{#if this.hasBody}}
        <div class="mt-4 flex flex-col items-center gap-10">
          <EmptyStatePoints @items={{@points}} />
          <EmptyStateActions
            @learnMoreUrl={{@learnMoreUrl}}
            @learnMoreAriaLabel={{this.learnMoreAriaLabel}}
            @createRoute={{@createRoute}}
            @createLabel={{this.createLabel}}
            @createColor={{@createColor}}
            @onCreate={{@onCreate}}
            @contactUrl={{@contactUrl}}
          />
        </div>
      {{/if}}
      {{yield}}
    </div>
  </template>
}
